import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import { KibButtonNew } from '@chewy/kib-controls-react'
import KibDivider from '@chewy/kib-divider-react/legacy'
import { KibField } from '@chewy/kib-fields-new-react'
import {
  KibAddFillIcon,
  KibArrowRightIcon,
  KibAttachIcon,
} from '@chewy/kib-icons-react'
import { KibSkeletonText } from '@chewy/kib-skeleton-react'
import { AlertIconType, Defaults } from '@pbt/pbt-ui-components'

import type { ManualAttachmentFile } from '~/api/graphql/generated/types'
import { RhapKibAttachmentsList } from '~/components/common/attachments/RhapKibAttachmentsList'
import MultiFileSelector from '~/components/common/inputs/MultiFileSelector'
import { KibUserSelect } from '~/components/common/inputs/user-select/KibUserSelect'
import { UserSelectFilterScope } from '~/components/common/inputs/UserSelect'
import { useAddedToAppointment } from '~/components/common/labels/AddedToAppointmentLabel'
import Expander from '~/components/common/lists/Expander'
import PatientDetailsHeader from '~/components/dashboard/soapV2/summary/patient-details/PatientDetailsHeader'
import { StatusError } from '~/components/elements/Status/Status'
import DialogNames from '~/constants/DialogNames'
import DocumentDialogStates from '~/constants/DocumentDialogStates'
import { OrderType } from '~/constants/SOAPStates'
import { getPatient } from '~/store/reducers/patients'
import { isFieldValuesChanged } from '~/utils'
import { getApolloErrorMessage } from '~/utils/errors'
import useDialog from '~/utils/useDialog'

import ImagingStatusSelect from '../../imaging-procedures/ImagingStatusSelect'
import ModalitySelect from '../../imaging-procedures/ModalitySelect'
import NotesTemplateInput from '../../template-inputs/NotesTemplateInput'
import { useDeleteManualAttachmentFile } from './useDeleteManualAttachmentFile'
import { useImagingAttachments } from './useImagingAttachments'
import { useImagingDetails } from './useImagingDetails'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import '@chewy/kib-skeleton-styles/dist/styles/kib-skeleton-text.css'
import '@chewy/kib-divider-styles/dist/kib-divider.css'
import styles from './RhapKibImagingDetails.module.scss'

const IMAGERY_MAX_ATTACHMENTS = 30

export interface RhapKibImagingDetailsProps {
  item: string
  onClose: () => void
}

const NotesActions: FC<{
  fileCount: number
  onAttachmentSelect: (files: File[]) => void
  onPlusButtonClick: () => void
}> = ({ onPlusButtonClick, onAttachmentSelect, fileCount }) => {
  const { t } = useTranslation('Common')

  return (
    <>
      <KibButtonNew
        emphasis="tertiary"
        icon={<KibAddFillIcon />}
        size="small"
        theme="utility"
        onClick={onPlusButtonClick}
      >
        {t('Common:ADD_NOTES_TEMPLATE')}
      </KibButtonNew>
      <MultiFileSelector onSelected={onAttachmentSelect}>
        <KibButtonNew
          disabled={fileCount >= IMAGERY_MAX_ATTACHMENTS}
          emphasis="tertiary"
          icon={<KibAttachIcon />}
          size="small"
          theme="utility"
        >
          {t('Common:ADD_ATTACHMENT')}
        </KibButtonNew>
      </MultiFileSelector>
    </>
  )
}

// eslint-disable-next-line react/no-multi-comp
const RhapKibImagingDetails = ({
  item: itemId,
  onClose,
}: RhapKibImagingDetailsProps) => {
  const { t } = useTranslation('Common')

  const {
    isSaving,
    isFetching,
    isLoadingDashboardRecord,
    clientId,
    patientId,
    order,
    appointment,
    vendorId,
    businessId,
    canViewImages,
    fields,
    saveOrder: handleSaveOrder,
    viewImages: handleViewImages,
    openInvoice: handleOpenInvoice,
  } = useImagingDetails(itemId, onClose)

  const patient = useSelector(getPatient(patientId))
  const { appointmentName, url } = useAddedToAppointment({
    appointment,
    soapId: order?.soapId,
  })

  const { attachments, refetch: refetchAttachments } = useImagingAttachments(
    order?.id,
  )

  const {
    deleteAttachment,
    loading: isDeleting,
    error: deleteError,
  } = useDeleteManualAttachmentFile()

  const [openImagingAttachmentDialog] = useDialog(
    DialogNames.IMAGING_ATTACHMENT_DIALOG,
  )
  const [openFileAlert] = useDialog(DialogNames.DISMISSIBLE_ALERT)
  const [openDocumentDialog] = useDialog(DialogNames.DOCUMENT)

  const { name, modalityId, assignedVetId, notes, statusId } = fields

  const fileCount = attachments.length

  const handlePreviewAttachment = (file: ManualAttachmentFile) => {
    openDocumentDialog({
      previewOnly: true,
      document: file,
      PreviewProps: {
        hideTopButtons: true,
        view: true,
        isSOAP: true,
        showChangeFile: false,
      },
      step: DocumentDialogStates.PREVIEW,
    })
  }

  const handleDeleteAttachment = async (file: ManualAttachmentFile) => {
    await deleteAttachment(file.id)
    await refetchAttachments()
  }

  const handleSelectedFiles = (selectedFiles: File[]) => {
    if (selectedFiles.length + fileCount > IMAGERY_MAX_ATTACHMENTS) {
      openFileAlert({
        iconType: AlertIconType.WARN,
        message: (
          <>
            {t('Common:INPUTS.FILE_SELECTOR.ALERT_MESSAGE_COUNT_CUSTOM', {
              fileLimit: IMAGERY_MAX_ATTACHMENTS,
            })}
            &nbsp;
            <a href={`mailto:${Defaults.SUPPORT_EMAIL}`}>
              {Defaults.SUPPORT_EMAIL}
            </a>
            .
          </>
        ),
      })
    } else {
      const fileURLsMetadata = selectedFiles.map((file) => ({
        url: URL.createObjectURL(file),
        fileName: file.name,
        fileType: file.type,
      }))
      openImagingAttachmentDialog({
        maxLimit: IMAGERY_MAX_ATTACHMENTS,
        initialFilesMetadata: fileURLsMetadata,
        initialTitle: order?.name,
        orderId: order?.id,
        businessId: businessId || '',
        clientId,
        patientId,
        imagingDashboardRecordId: itemId,
        preexistingFileCount: fileCount,
        onAttachmentsUploaded: refetchAttachments,
      })
    }
  }

  const saveOrder = () => {
    handleSaveOrder()
  }

  const viewImages = () => {
    handleViewImages()
  }

  const backToDashboard = () => onClose()

  return (
    <Expander
      isChirp
      additionalButtons={[
        Boolean(order?.invoiceId) && (
          <KibButtonNew
            disabled={isSaving || isLoadingDashboardRecord}
            emphasis="primary"
            key="view-invoice"
            size="medium"
            theme="action"
            onClick={() =>
              handleOpenInvoice({
                clientId,
                patientId,
                invoiceId: order?.invoiceId,
                soapId: order?.soapId,
                logId: order?.logId,
                logType: OrderType.PROCEDURE,
              })
            }
          >
            {t('Common:VIEW_INVOICE')}
          </KibButtonNew>
        ),
      ]}
      expandedItemClass="imaging-request"
      getUnsavedData={() => isFieldValuesChanged(fields)}
      isFetching={isFetching}
      isSaving={isSaving || isLoadingDashboardRecord}
      onBack={backToDashboard}
      onSaveRequested={saveOrder}
    >
      <div className={styles.container}>
        {/* Patient Section */}
        {clientId && patientId && patient && (
          <div className={styles.patientSection}>
            <PatientDetailsHeader clientId={clientId} patient={patient} />
            <KibDivider className={styles.headerDivider} color="alternate" />
          </div>
        )}

        {/* Title Section */}
        <div className={styles.header}>
          {isFetching ? (
            <div className={styles.titleSkeleton}>
              <KibSkeletonText />
            </div>
          ) : (
            <span className={styles.title}>{order?.name}</span>
          )}
          {/* Status Section */}
          <div className={styles.statusSection}>
            <ImagingStatusSelect
              isChirp
              field={statusId}
              isSoapFinalized={Boolean(order?.soapFinalized)}
            />
          </div>
        </div>

        {/* Order Details Section */}
        <div className={styles.orderDetailsSection}>
          <h2 className={styles.orderDetailsTitle}>
            {t('Common:ORDER_DETAILS')}
          </h2>
          <div className={styles.orderDetailsGrid}>
            {/* TODO: Print label Section - we will not have this print label in scope of this feature toggle */}
            {/* <div className={styles.orderDetailItem}>
              <span>{t('Common:IMAGING_ORDER')}:</span>
              <button
                className={styles.chirpLink}
                type="button"
                onClick={() => {}}
              >
                {t('Common:PRINT_LABEL')}
              </button>
            </div> */}
            {/* Appointment Section */}
            {appointment && order?.soapId && (
              <div className={styles.orderDetailItem}>
                <span>{t('Common:SOURCE')}:</span>
                <RouterLink className={styles.chirpLink} to={url}>
                  {appointmentName}
                </RouterLink>
              </div>
            )}
            {/* Go to Study Section - Navigate to imaging with provider */}
            {canViewImages && (
              <div
                className={styles.goToStudySection}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation()
                  viewImages()
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    viewImages()
                  }
                }}
              >
                <KibButtonNew
                  aria-label={t('Common:VIEW_IMAGES')}
                  className={styles.goToStudyButton}
                  emphasis="tertiary"
                  icon={<KibArrowRightIcon />}
                  iconPosition="start"
                  size="small"
                  theme="action"
                  title={t('Common:VIEW_IMAGES')}
                >
                  {t('Common:VIEW_IMAGES')}
                </KibButtonNew>
              </div>
            )}
          </div>
        </div>

        {/* Assigned Vet Section */}
        <div className={styles.assignedVetSection}>
          <KibUserSelect
            disabled
            filterScope={UserSelectFilterScope.Doctors}
            label={`${t('Common:ASSIGN_TO')}*`}
            placeHolder={t('Common:SELECT_ACTION')}
            userSelectStateId={`imaging-assigned-vet-${itemId}`}
            value={assignedVetId.value}
            onChange={(value) => assignedVetId.setValue(value)}
          />
        </div>

        {/* Name and Modality Row */}
        <div className={styles.inputRow}>
          <KibField
            invalid={!name.valid}
            label={name.label || ''}
            messagesContent={name.message}
            renderControl={(props) => (
              <input
                {...props}
                disabled={isFetching}
                type="text"
                value={name.value}
                onChange={(e) => name.setValue(e.target.value)}
              />
            )}
          />
          <ModalitySelect isChirp field={modalityId} vendorId={vendorId} />
        </div>

        {/* Notes Section */}
        <div className={styles.notesSection}>
          <NotesTemplateInput
            hidePanel
            hidePlusButtonBlock
            isChirp
            resetStateOnValueChange
            // eslint-disable-next-line react/no-unstable-nested-components
            actions={(onPlusButtonClick: () => void) => (
              <NotesActions
                fileCount={fileCount}
                onAttachmentSelect={handleSelectedFiles}
                onPlusButtonClick={onPlusButtonClick}
              />
            )}
            field={notes}
            minEditorHeight={64}
            minHeight={2}
            placeholder={notes.label}
            title={t('Common:NOTES')}
          />
        </div>

        {/* Attachments Section */}
        <RhapKibAttachmentsList
          className={styles.attachmentsTitle}
          files={attachments || []}
          isDeleting={isDeleting}
          onDelete={handleDeleteAttachment}
          onPreview={handlePreviewAttachment}
        />
        {deleteError !== undefined && (
          <StatusError>{getApolloErrorMessage(deleteError)}</StatusError>
        )}
      </div>
    </Expander>
  )
}

export default RhapKibImagingDetails
